/**
 * Tracks the number of open tabs of the application using BroadcastChannel.
 *
 * Each tab periodically broadcasts a heartbeat with its unique ID.
 * Every tab maintains a map of recently seen peers, and the count is
 * derived from how many IDs have been heard within the stale threshold.
 * Tabs that crash or close without firing beforeunload simply stop
 * sending heartbeats and are pruned automatically.
 */

const CHANNEL_NAME = 'videa_tab_counter';
const HEARTBEAT_INTERVAL_MS = 5_000; // 5 seconds
const STALE_THRESHOLD_MS = 12_000; // 12 seconds (> 2 heartbeats)

const TAB_ID =
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Map of peer tab ID → last seen timestamp */
const peerTabs = new Map<string, number>();

let initialized = false;
let channel: BroadcastChannel | null = null;

interface TabMessage {
  type: 'heartbeat' | 'close';
  id: string;
}

function broadcastHeartbeat(): void {
  try {
    channel?.postMessage({ type: 'heartbeat', id: TAB_ID } satisfies TabMessage);
  } catch {
    // Channel may be closed
  }
}

function broadcastClose(): void {
  try {
    channel?.postMessage({ type: 'close', id: TAB_ID } satisfies TabMessage);
  } catch {
    // Best-effort
  }
}

function handleMessage(event: MessageEvent<TabMessage>): void {
  const { type, id } = event.data;
  if (type === 'close') {
    peerTabs.delete(id);
  } else if (type === 'heartbeat') {
    peerTabs.set(id, Date.now());
  }
}

/**
 * Initialize tab tracking. Safe to call multiple times — only registers once.
 */
export function initTabCounter(): void {
  if (initialized) return;

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = handleMessage;
  } catch {
    // BroadcastChannel unavailable (e.g. some privacy-focused browsers)
    // Set initialized so we don't retry — if the browser lacks support, retrying won't help
    initialized = true;
    return;
  }

  initialized = true;

  broadcastHeartbeat();
  setInterval(broadcastHeartbeat, HEARTBEAT_INTERVAL_MS);
  window.addEventListener('beforeunload', broadcastClose);
}

/**
 * Returns the number of currently open tabs of this application.
 * Automatically initializes tracking on first call if not already initialized.
 */
export function getOpenTabCount(): number {
  if (!initialized) {
    initTabCounter();
  }

  // Prune stale peers
  const now = Date.now();
  for (const [id, timestamp] of peerTabs) {
    if (now - timestamp > STALE_THRESHOLD_MS) {
      peerTabs.delete(id);
    }
  }

  // +1 for the current tab (we don't receive our own broadcasts)
  return peerTabs.size + 1;
}
